// CompletedTaskView.js - Simplified view for completed tasks with download functionality
import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, getBlob } from 'firebase/storage';
import { db, storage, TASK_STATUS } from '../firebase.config.js';
import './CompletedTaskView.css';

const CompletedTaskView = ({ task, taskId, user }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  
  // Only count and download the final images based on task status
  let finalImages = [];
  
  // If task has L2 process and is validated L2, download L2 images only
  if (task.status === TASK_STATUS.VALIDATED_L2) {
    finalImages = task.images?.filter(img => img.isL2Image && img.status === 'approved') || [];
  }
  // If task is validated L1 (no L2 process), download L1 images
  else if (task.status === TASK_STATUS.VALIDATED_L1) {
    finalImages = task.images?.filter(img => !img.isL2Image && img.status === 'approved') || [];
  }
  
  const totalImages = finalImages.length;
  const downloadDate = task.lastDownloadedAt?.toDate?.();
  const downloadedBy = task.lastDownloadedBy;

  const generateZipFileName = () => {
    const taskName = task.taskName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'task';
    const date = new Date().toISOString().split('T')[0];
    const phase = task.status === TASK_STATUS.VALIDATED_L2 ? 'L2' : 'L1';
    return `${taskName}_${phase}_${totalImages}_images_${date}.zip`;
  };

  const downloadImagesAsZip = async () => {
    if (isDownloading || finalImages.length === 0) return;
    
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      // Import JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      // Download each image using Firebase Storage SDK
      for (let i = 0; i < finalImages.length; i++) {
        const image = finalImages[i];
        
        try {
          setDownloadProgress(Math.round(((i + 1) / finalImages.length) * 90));
          
          // Get the storage path from the image object
          let storagePath;
          
          if (image.path) {
            // Use the stored path directly
            storagePath = image.path;
          } else if (image.fileName && taskId) {
            // Reconstruct path from fileName
            storagePath = `tasks/${taskId}/${image.fileName}`;
          } else if (image.name && taskId) {
            // Fallback pour les anciennes images - essayer de deviner l'extension depuis l'originalName
            const extension = image.originalName ? image.originalName.split('.').pop() : 'jpg';
            storagePath = `tasks/${taskId}/${image.name}.${extension}`;
          } else {
            // Last resort: extract from URL
            try {
              const urlParts = image.url.split('/o/')[1];
              storagePath = decodeURIComponent(urlParts.split('?')[0]);
            } catch (e) {
              console.error(`Cannot extract path for ${image.name}`);
              continue;
            }
          }
          
          // Download using Firebase Storage SDK
          const storageRef = ref(storage, storagePath);
          const blob = await getBlob(storageRef);
          
          // Add to zip - utiliser fileName (avec extension) ou originalName comme fallback
          const filename = image.fileName || image.originalName || `image_${i + 1}.jpg`;
          zip.file(filename, blob);
          
        } catch (error) {
          console.error(`Error downloading ${image.name}:`, error);
          // Continue with other images
        }
      }
      
      // Generate zip
      setDownloadProgress(95);
      const content = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      // Download the zip
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = generateZipFileName();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Cleanup
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
      setDownloadProgress(100);
      
      // Update download info
      await updateDoc(doc(db, 'taskbacklog', taskId), {
        lastDownloadedAt: serverTimestamp(),
        lastDownloadedBy: user.email,
        downloadCount: (task.downloadCount || 0) + 1
      });
      
      // Reload to show updated info
      setTimeout(() => window.location.reload(), 1500);
      
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const getPhaseLabel = () => {
    return task.status === TASK_STATUS.VALIDATED_L2 ? 'L2' : 'L1';
  };

  return (
    <div className="completed">
      <div className="completed__container">
        {/* Header */}
        <div className="completed__header">
          <div className="completed__icon">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <h1 className="completed__title">{task.taskName}</h1>
          <p className="completed__subtitle">Task completed successfully - {getPhaseLabel()} images ready</p>
        </div>

        {/* Images Count */}
        <div className="completed__count">
          <span className="completed__count-number">{totalImages}</span>
          <span className="completed__count-label">{getPhaseLabel()} images ready</span>
        </div>

        {/* Download History */}
        {downloadDate && (
          <div className="completed__history">
            <svg className="completed__history-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 8v4l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span className="completed__history-text">
              Last downloaded {downloadDate.toLocaleDateString()} by {downloadedBy}
            </span>
          </div>
        )}

        {/* Download Button */}
        <div className="completed__download">
          {!isDownloading ? (
            <button 
              className="completed__button"
              onClick={downloadImagesAsZip}
              disabled={totalImages === 0}
            >
              <svg className="completed__button-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="7 10 12 15 17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download {getPhaseLabel()} ZIP
            </button>
          ) : (
            <div className="completed__progress">
              <div className="completed__progress-bar">
                <div 
                  className="completed__progress-fill"
                  style={{ width: `${downloadProgress}%` }}
                />
              </div>
              <span className="completed__progress-text">{downloadProgress}%</span>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="completed__metadata">
          <div className="completed__metadata-item">
            <span className="completed__metadata-label">Task ID</span>
            <span className="completed__metadata-value">{taskId.substring(0, 8)}...</span>
          </div>
          <div className="completed__metadata-item">
            <span className="completed__metadata-label">Phase</span>
            <span className="completed__metadata-value">{getPhaseLabel()}</span>
          </div>
          <div className="completed__metadata-item">
            <span className="completed__metadata-label">Downloads</span>
            <span className="completed__metadata-value">{task.downloadCount || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletedTaskView;